import React, {useEffect, useRef, useState} from 'react';
import {useTranslation} from 'react-i18next';
import {Switch} from '../../buttons/Swtich';
import {DotsThree} from '../../icons';

// Alto aproximado del menú abierto. Si debajo del botón no entra, abre hacia arriba: la última fila
// de la lista lo dejaba cortado contra el borde de la ventana.
const MENU_HEIGHT_PX = 360;

export const MENU_EDIT = 'edit';
export const MENU_DUPLICATE = 'duplicate';
export const MENU_VIEW_TEXT = 'view_text';
export const MENU_COPY_TEXT = 'copy_text';
export const MENU_DEACTIVATE = 'deactivate';

/**
 * El menú «···» de una fila propia (contrato §8): quién lo creó, y Editar · Duplicar… · Ver el texto ·
 * Copiar el texto · Publicar en el portal · Desactivar.
 *
 * Desactivar se muestra siempre, y deshabilitado para quien no lo creó, con el motivo ESCRITO debajo
 * y no en un tooltip: en un teléfono no hay hover, y un botón gris sin explicación se lee como una
 * falla. Publicar en el portal es un interruptor que no cierra el menú, para ver el estado nuevo.
 */
export function PromptRowMenu({prompt, disabled = false, onAction, onTogglePortal}) {
    const {t} = useTranslation();
    const [open, setOpen] = useState(false);
    const [upward, setUpward] = useState(false);
    const rootRef = useRef(null);
    const triggerRef = useRef(null);

    useEffect(() => {
        if (!open) return undefined;
        const handleMouseDown = (event) => {
            if (rootRef.current && !rootRef.current.contains(event.target)) setOpen(false);
        };
        const handleKeyDown = (event) => {
            if (event.key !== 'Escape') return;
            setOpen(false);
            if (triggerRef.current) triggerRef.current.focus();
        };
        document.addEventListener('mousedown', handleMouseDown);
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('mousedown', handleMouseDown);
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [open]);

    const toggle = () => {
        if (!open && triggerRef.current) {
            const rect = triggerRef.current.getBoundingClientRect();
            setUpward(window.innerHeight - rect.bottom < MENU_HEIGHT_PX && rect.top > MENU_HEIGHT_PX);
        }
        setOpen((value) => !value);
    };

    // El foco vuelve al «···» antes de la acción: el menú se desmonta y, sin esto, caía a `<body>`.
    // Si la acción abre un modal, reactstrap lo devuelve ahí al cerrarlo.
    const choose = (action) => () => {
        setOpen(false);
        if (triggerRef.current) triggerRef.current.focus();
        onAction(action, prompt);
    };

    const canEdit = Boolean(prompt.is_editable);
    const canDeactivate = Boolean(prompt.is_deletable);
    const isAgencyWide = !prompt.client;

    return (
        <div className="agp-menu" ref={rootRef}>
            <button type="button" ref={triggerRef} className="agp-menu__trigger" onClick={toggle}
                    disabled={disabled} aria-expanded={open} aria-controls={`agp-menu-${prompt.id}`}
                    aria-label={t('agency_prompts.menu_aria', {title: prompt.title, interpolation: {escapeValue: false}})}>
                <DotsThree size={20} weight="bold"/>
            </button>
            {open && (
                <div id={`agp-menu-${prompt.id}`} className={`agp-menu__panel${upward ? ' -open-up' : ''}`}>
                    {/* Quién lo creó vive acá y no en la fila: repetido en cada fila no decía nada. */}
                    <div className="agp-menu__meta">
                        {prompt.created_by && prompt.created_by.name
                            ? t('agency_prompts.created_by', {name: prompt.created_by.name, interpolation: {escapeValue: false}})
                            : t('agency_prompts.created_by_team')}
                    </div>
                    <div className="agp-menu__separator"/>
                    {canEdit && (
                        <button type="button" className="agp-menu__item" onClick={choose(MENU_EDIT)}>
                            {t('agency_prompts.menu_edit')}
                        </button>
                    )}
                    <button type="button" className="agp-menu__item" onClick={choose(MENU_DUPLICATE)}>
                        {t('agency_prompts.menu_duplicate')}
                    </button>
                    <button type="button" className="agp-menu__item" onClick={choose(MENU_VIEW_TEXT)}>
                        {t('agency_prompts.menu_view_text')}
                    </button>
                    <button type="button" className="agp-menu__item" onClick={choose(MENU_COPY_TEXT)}>
                        {t('agency_prompts.menu_copy_text')}
                    </button>
                    {canEdit && (
                        <div className="agp-menu__switch">
                            <div className="agp-menu__switch-text">
                                <span id={`agp-portal-${prompt.id}`}>{t('agency_prompts.menu_portal')}</span>
                                {isAgencyWide && <small>{t('agency_prompts.menu_portal_hint_agency')}</small>}
                            </div>
                            <Switch name={`agp-portal-${prompt.id}`} checked={Boolean(prompt.portal_visible)}
                                    aria-labelledby={`agp-portal-${prompt.id}`}
                                    onChange={(event) => onTogglePortal(prompt, event.target.checked)}
                                    style={{margin: 0}}/>
                        </div>
                    )}
                    <div className="agp-menu__separator"/>
                    <button type="button" className="agp-menu__item -danger"
                            onClick={choose(MENU_DEACTIVATE)} disabled={!canDeactivate}
                            aria-describedby={canDeactivate ? undefined : `agp-locked-${prompt.id}`}>
                        {t('agency_prompts.menu_deactivate')}
                        {!canDeactivate && (
                            <small id={`agp-locked-${prompt.id}`}>{t('agency_prompts.menu_deactivate_locked')}</small>
                        )}
                    </button>
                </div>
            )}
        </div>
    );
}
